import { HttpClient } from '@shared/adapters/HttpClient';
import { EntryReceiptMovement, EntryReceiptRequirement } from '../../models/Entry';

export interface AcceptProductRequest {
  product_id: number;
  location_code: string;
  qty: number;
  batch_id?: number;
}

export interface RejectProductRequest {
  product_id: number;
  qty: number;
  rejection_type: string;
  rejection_text?: string;
  attachments?: File[];
}

export interface DispatchByLocationLine {
  group_code: string;
  product_id: number;
  qty_expected: number;
  work_type: 'LABELING' | 'BAGGING' | 'BOTH';
}

export interface DispatchByLocationRequest {
  location_id: number;
  dispatch_type: 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER';
  lines?: DispatchByLocationLine[];
}

export interface PendingDispatchLocation {
  location_id: number;
  location_code: string;
  pending_movements: number;
  total_qty: number;
  products: Array<{
    product_id: number;
    product_name: string;
    product_code?: string;
    product_image?: string;
    qty: number;
  }>;
}

export interface SeparationTimeProduct {
  product_id: number;
  qty: number;
}

export interface RegisterSeparationTimeRequest {
  products: SeparationTimeProduct[];
  time_minutes: number;
}

export interface ReceiptNoteAttachment {
  id: number;
  url: string;
  mime_type: string;
  file_name: string;
  sort_order: number;
}

export interface ReceiptNoteAuthor {
  id: number;
  name: string;
  email: string;
}

export interface ReceiptNote {
  id: number;
  entry_receipt_id: number;
  user_id: number;
  body: string | null;
  created_at: string;
  updated_at: string;
  attachments: ReceiptNoteAttachment[];
  author: ReceiptNoteAuthor;
}

export interface ReceiptNotesResponse {
  data: ReceiptNote[];
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  links?: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
}

export class ReceiptApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Aceptar productos
   */
  async acceptProduct(entryId: number, receiptId: number, data: AcceptProductRequest): Promise<void> {
    await this.httpClient.post(`/entries/${entryId}/receipts/${receiptId}/acceptances`, data);
  }

  /**
   * Rechazar productos (multipart/form-data)
   */
  async rejectProduct(entryId: number, receiptId: number, data: RejectProductRequest): Promise<void> {
    const formData = new FormData();
    formData.append('product_id', data.product_id.toString());
    formData.append('qty', data.qty.toString());
    formData.append('rejection_type', data.rejection_type);

    if (data.rejection_text) {
      formData.append('rejection_text', data.rejection_text);
    }

    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file, file.name);
      });
    }

    // No establecer Content-Type manualmente - Axios lo hace automáticamente con el boundary correcto.
    await this.httpClient.postMultipart(`/entries/${entryId}/receipts/${receiptId}/rejections`, formData);
  }

  /**
   * Despachar por ubicación
   */
  async dispatchByLocation(entryId: number, receiptId: number, data: DispatchByLocationRequest): Promise<void> {
    // Clonar data y convertir location_id a string para evitar errores de validación
    const payload = {
      ...data,
      location_id: String(data.location_id),
    };

    await this.httpClient.post(`/entries/${entryId}/receipts/${receiptId}/dispatch-by-location`, payload);
  }

  /**
   * Completar albarán
   */
  async completeReceipt(entryId: number, receiptId: number): Promise<void> {
    await this.httpClient.post(`/entries/${entryId}/receipts/${receiptId}/complete`, {});
  }

  /**
   * Cancelar albarán
   */
  async cancelReceipt(entryId: number, receiptId: number, data: { cancel_reason: string }): Promise<void> {
    await this.httpClient.put(`/entries/${entryId}/receipts/${receiptId}/cancel`, data);
  }

  /**
   * Obtener movimientos del albarán
   */
  async getMovements(
    entryId: number,
    receiptId: number,
    params?: {
      location_id?: number;
      /** Por defecto 100 (listado de movimientos del albarán). */
      per_page?: number;
    },
  ): Promise<EntryReceiptMovement[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('per_page', String(params?.per_page ?? 100));
    if (params?.location_id) {
      queryParams.append('location_id', params.location_id.toString());
    }

    const queryString = queryParams.toString();
    const url = `/entries/${entryId}/receipts/${receiptId}/movements${queryString ? `?${queryString}` : ''}`;

    const response = await this.httpClient.get<{
      data: EntryReceiptMovement[];
      meta: {
        total: number;
        perPage: number;
        currentPage: number;
        lastPage: number;
      };
      links: {
        first: string;
        prev: string | null;
        next: string | null;
        last: string;
      };
    }>(url);

    // Extraer el array de movimientos de la respuesta paginada
    return response.data || [];
  }

  /**
   * Eliminar movimiento del albarán por ID de movimiento
   */
  async deleteMovement(entryId: number, receiptId: number, movementId: number): Promise<void> {
    await this.httpClient.delete(`/entries/${entryId}/receipts/${receiptId}/movements/${movementId}`);
  }

  /**
   * Obtener ubicaciones pendientes de despacho
   */
  async getPendingDispatchLocations(entryId: number, receiptId: number): Promise<PendingDispatchLocation[]> {
    return await this.httpClient.get<PendingDispatchLocation[]>(
      `/entries/${entryId}/receipts/${receiptId}/pending-dispatch-locations`,
    );
  }

  /**
   * Obtener órdenes de ubicación creadas para una recepción
   */
  async getLocationOrders(
    entryId: number,
    receiptId: number,
    params?: {
      status?: string;
      order_type?: string;
      page?: number;
      per_page?: number;
    },
  ): Promise<{
    data: Array<{
      id: number;
      receipt_id: number;
      location_id: number;
      location_code: string;
      dispatch_type: 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER';
      status: string;
      created_at: string;
      products?: Array<{
        product_id: number;
        product_name: string;
        qty: number;
      }>;
    }>;
    meta: {
      total: number;
      perPage: number;
      currentPage: number;
      lastPage: number;
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.order_type) queryParams.append('order_type', params.order_type);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());

    const queryString = queryParams.toString();
    const url = `/entries/${entryId}/receipts/${receiptId}/location-orders${queryString ? `?${queryString}` : ''}`;

    return await this.httpClient.get(url);
  }

  /**
   * Crear/Actualizar requisito (multipart/form-data)
   */
  async createRequirement(
    entryId: number,
    receiptId: number,
    requirementCode: string,
    attachments?: File[],
  ): Promise<EntryReceiptRequirement> {
    const formData = new FormData();

    formData.append('entry_id', entryId.toString());
    formData.append('receipt_id', receiptId.toString());
    formData.append('requirementCode', requirementCode);

    if (attachments && attachments.length > 0) {
      attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file, file.name);
      });
    }

    return await this.httpClient.postMultipart<EntryReceiptRequirement>(
      `/entries/${entryId}/receipts/${receiptId}/requirements/${requirementCode}`,
      formData,
    );
  }

  /**
   * Eliminar requisito
   */
  async deleteRequirement(entryId: number, receiptId: number, requirementCode: string): Promise<void> {
    await this.httpClient.delete(`/entries/${entryId}/receipts/${receiptId}/requirements/${requirementCode}`);
  }

  /**
   * Obtener carritos de un receipt
   */
  async getCarts(
    entryId: number,
    receiptId: number,
  ): Promise<{
    data: Array<{
      id: number;
      code: string;
      total_products: number;
      total_quantity: number;
      has_dispatch: boolean;
      dispatch_type: 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER' | null;
      dispatch_id: number | null;
    }>;
  }> {
    return await this.httpClient.get<{
      data: Array<{
        id: number;
        code: string;
        total_products: number;
        total_quantity: number;
        has_dispatch: boolean;
        dispatch_type: 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER' | null;
        dispatch_id: number | null;
      }>;
    }>(`/entries/${entryId}/receipts/${receiptId}/carts`);
  }

  /**
   * Registrar tiempo de separación
   */
  async registerSeparationTime(entryId: number, receiptId: number, data: RegisterSeparationTimeRequest): Promise<void> {
    await this.httpClient.post(`/entries/${entryId}/receipts/${receiptId}/separation-times`, data);
  }

  /**
   * Actualizar tiempo de separación (reemplaza productos y tiempo total)
   */
  async updateSeparationTime(
    entryId: number,
    receiptId: number,
    separationTimeId: number,
    data: RegisterSeparationTimeRequest,
  ): Promise<void> {
    await this.httpClient.put(`/entries/${entryId}/receipts/${receiptId}/separation-times/${separationTimeId}`, data);
  }

  /**
   * Obtener histórico de notas de una recepción
   */
  async getNotes(
    entryId: number,
    receiptId: number,
    params?: {
      page?: number;
      per_page?: number;
    },
  ): Promise<ReceiptNotesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());

    const queryString = queryParams.toString();
    const url = `/entries/${entryId}/receipts/${receiptId}/notes${queryString ? `?${queryString}` : ''}`;

    return await this.httpClient.get<ReceiptNotesResponse>(url);
  }

  /**
   * Crear nota de recepción (multipart/form-data)
   */
  async createNote(
    entryId: number,
    receiptId: number,
    data: {
      body?: string;
      attachments?: File[];
    },
  ): Promise<ReceiptNote> {
    const formData = new FormData();

    if (data.body && data.body.trim().length > 0) {
      formData.append('body', data.body.trim());
    }

    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file, file.name);
      });
    }

    return await this.httpClient.postMultipart<ReceiptNote>(
      `/entries/${entryId}/receipts/${receiptId}/notes`,
      formData,
    );
  }

  /**
   * Eliminar nota de recepción
   */
  async deleteNote(entryId: number, receiptId: number, noteId: number): Promise<void> {
    await this.httpClient.delete(`/entries/${entryId}/receipts/${receiptId}/notes/${noteId}`);
  }
}
